import jsPDF from 'jspdf';
import autoTable from 'jspdf-autotable';

const getCurrency = (settings) => {
  if (settings?.currency === '₹') return 'INR';
  return settings?.currency || 'INR';
};

const loadImage = (url) => {
  return new Promise((resolve) => {
    const img = new Image();
    img.crossOrigin = 'Anonymous';
    img.onload = () => resolve(img);
    img.onerror = () => resolve(null);
    img.src = url;
  });
};

export const generateTaxInvoice = async (invoiceData, business, customer, includeGst, gstRate) => {
  const doc = new jsPDF();
  
  // Premium Theme Colors
  const primaryColor = [180, 84, 246]; // Purple/Violet
  const darkText = [30, 30, 30];
  const grayText = [120, 120, 120];
  const borderColor = [230, 230, 230];

  const drawCard = (x, y, w, h, title, details) => {
    doc.setFillColor(255, 255, 255);
    doc.setDrawColor(...borderColor);
    doc.roundedRect(x, y, w, h, 3, 3, 'FD');
    
    doc.setFillColor(...primaryColor);
    doc.rect(x + 4, y + 4, 2, 8, 'F');
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...primaryColor);
    doc.text(title, x + 10, y + 10);
    
    doc.setTextColor(...darkText);
    let currentY = y + 16;
    details.forEach(line => {
      if (!line.text) return;
      if (line.bold) doc.setFont('helvetica', 'bold');
      else doc.setFont('helvetica', 'normal');
      doc.setFontSize(line.size || 10);
      
      let textX = x + 8;
      if (line.highlight) {
        doc.setFont('helvetica', 'bold');
        doc.text(line.highlight, textX, currentY);
        textX += doc.getTextWidth(line.highlight);
        doc.setFont('helvetica', 'normal');
      }
      
      const splitText = doc.splitTextToSize(line.text, w - (textX - x) - 4);
      doc.text(splitText, textX, currentY);
      currentY += (splitText.length * 4) + (line.space || 1);
    });
  };

  let logoOffset = 0;
  if (business?.assets?.logoUrl) {
    try {
      const img = await loadImage(business.assets.logoUrl);
      if (img) {
        doc.addImage(img, 'PNG', 14, 15, 20, 20);
        logoOffset = 25;
      }
    } catch (e) {}
  }

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  doc.setTextColor(...darkText);
  const rawName = (business?.name || 'Your Business').toUpperCase();
  const splitName = doc.splitTextToSize(rawName, 110);
  doc.text(splitName, 14 + logoOffset, 22);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...primaryColor);
  const taxTaglineY = 22 + (splitName.length * 6);
  doc.text('OFFICIAL TAX INVOICE • BILLING DOCUMENT', 14 + logoOffset, taxTaglineY);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(24);
  doc.setTextColor(...primaryColor);
  doc.text(includeGst ? 'TAX INVOICE' : 'INVOICE', 196, 22, { align: 'right' });
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(10);
  doc.setTextColor(...grayText);
  doc.text(invoiceData.invoiceNumber || 'NEW', 196, 28, { align: 'right' });

  const businessDetails = [
    { text: business?.name || 'Your Business', bold: true, size: 11 },
    { text: business?.address?.street || 'Local Street' },
    { text: business?.address?.city ? `${business.address.city}, ${business.address.state || ''}` : 'City, State' },
    { text: business?.address?.country || 'India' },
    { text: ' ', space: 2 },
    { highlight: 'CONTACT: ', text: business?.contact?.phone || 'N/A' },
    { highlight: 'EMAIL: ', text: business?.contact?.email || 'N/A' },
  ];
  
  if (business?.gstNumber) {
    businessDetails.push({ highlight: 'GSTIN: ', text: business.gstNumber });
  }

  const customerDetails = [
    { text: customer?.name || 'Customer Name', bold: true, size: 11 },
    { text: customer?.phone ? `Phone: ${customer.phone}` : '' },
    { text: customer?.email ? `Email: ${customer.email}` : '' },
    { text: customer?.address || '' }
  ];

  drawCard(14, 40, 85, 55, 'ISSUED BY', businessDetails);
  drawCard(105, 40, 85, 55, 'BILL TO', customerDetails);

  doc.setDrawColor(...borderColor);
  doc.line(14, 105, 196, 105);
  doc.line(14, 125, 196, 125);

  const drawMeta = (x, y, title, val) => {
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7);
    doc.setTextColor(...grayText);
    doc.text(title, x, y);
    
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9);
    doc.setTextColor(...darkText);
    doc.text(val, x, y + 5);
  };

  drawMeta(14, 112, 'ISSUE DATE', new Date(invoiceData.issueDate || invoiceData.createdAt || Date.now()).toLocaleDateString('en-GB'));
  drawMeta(60, 112, 'DUE DATE', invoiceData.dueDate ? new Date(invoiceData.dueDate).toLocaleDateString('en-GB') : 'Upon Receipt');
  drawMeta(105, 112, 'CURRENCY', getCurrency(business?.settings));
  drawMeta(150, 112, 'PAYMENT STATUS', invoiceData.status ? invoiceData.status.toUpperCase() : 'PENDING');

  const itemImages = {};
  const hasImages = invoiceData.items && invoiceData.items.some(i => i.imageUrl);
  if (hasImages) {
    await Promise.all(invoiceData.items.map(async (item, index) => {
      if (item.imageUrl) {
        try {
          const img = await loadImage(item.imageUrl);
          if (img) itemImages[index] = img;
        } catch (e) {}
      }
    }));
  }

  const hasHsn = invoiceData.items && invoiceData.items.some(i => i.hsnCode && i.hsnCode.trim() !== '');
  
  const headRow = hasImages ? ['IMG'] : [];
  headRow.push('DESCRIPTION');
  if (hasHsn) headRow.push('HSN/SAC');
  headRow.push('QTY', 'PRICE');
  if (includeGst) headRow.push('CGST', 'SGST');
  headRow.push('AMOUNT');
  
  const head = [headRow];

  const body = (invoiceData.items || []).map(item => {
    const row = [];
    if (hasImages) row.push(''); 
    row.push(item.name || 'Item');
    if (hasHsn) row.push(item.hsnCode || '-');
    row.push(item.quantity || 1);

    if (includeGst) {
      const itemTotal = (item.quantity || 1) * (item.unitPrice || 0);
      const cgst = (itemTotal * (gstRate / 2)) / 100;
      const sgst = (itemTotal * (gstRate / 2)) / 100;
      row.push(
        `${(item.unitPrice||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${cgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${sgst.toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${(item.total||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`
      );
    } else {
      row.push(
        `${(item.unitPrice||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`,
        `${(item.total||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`
      );
    }
    return row;
  });

  autoTable(doc, {
    startY: 135,
    head: head,
    body: body,
    theme: 'plain',
    headStyles: { 
      fillColor: primaryColor,
      textColor: [255, 255, 255],
      fontStyle: 'bold',
      fontSize: 9
    },
    bodyStyles: {
      fontSize: 9,
      textColor: darkText
    },
    alternateRowStyles: {
      fillColor: [252, 252, 252]
    },
    columnStyles: {
      0: hasImages ? { cellWidth: 15, halign: 'center' } : { cellWidth: 70 },
      1: hasImages ? { cellWidth: 55 } : undefined,
      [headRow.length - 1]: { halign: 'right', fontStyle: 'bold' }
    },
    didDrawCell: function(data) {
      if (hasImages && data.section === 'body' && data.column.index === 0) {
        const img = itemImages[data.row.index];
        if (img) {
          const imgSize = 10;
          const x = data.cell.x + (data.cell.width - imgSize) / 2;
          const y = data.cell.y + (data.cell.height - imgSize) / 2;
          doc.addImage(img, 'PNG', x, y, imgSize, imgSize);
        }
      }
    },
    didDrawPage: function(data) {
      doc.setDrawColor(...borderColor);
      doc.line(14, data.cursor.y, 196, data.cursor.y);
    }
  });

  const finalY = doc.lastAutoTable.finalY || 160;

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...grayText);
  
  doc.text('SUBTOTAL', 120, finalY + 15);
  doc.setTextColor(...darkText);
  doc.text(`${getCurrency(business?.settings)} ${(invoiceData.subtotal||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 15, { align: 'right' });

  if (includeGst) {
    doc.setTextColor(...grayText);
    doc.text(`GST (${gstRate}%)`, 120, finalY + 22);
    doc.setTextColor(...darkText);
    doc.text(`${getCurrency(business?.settings)} ${(invoiceData.taxAmount||0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 22, { align: 'right' });
  }

  if (invoiceData.discountAmount > 0) {
    doc.setTextColor(...grayText);
    doc.text('DISCOUNT', 120, finalY + 29);
    doc.setTextColor(34, 197, 94);
    doc.text(`- ${getCurrency(business?.settings)} ${invoiceData.discountAmount.toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 29, { align: 'right' });
  }

  let totalY = finalY + 35;
  
  if (invoiceData.advanceAmount > 0 || invoiceData.paidAmount > 0) {
    const paidAmt = invoiceData.advanceAmount || invoiceData.paidAmount || 0;
    doc.setTextColor(...grayText);
    doc.text('PAID AMOUNT', 120, finalY + 36);
    doc.setTextColor(34, 197, 94); 
    doc.text(`- ${getCurrency(business?.settings)} ${Number(paidAmt).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 196, finalY + 36, { align: 'right' });
    totalY += 10;
  }

  doc.setFillColor(...primaryColor);
  doc.roundedRect(110, totalY - 5, 86, 12, 2, 2, 'F');
  
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  doc.setTextColor(255, 255, 255);
  doc.text('TOTAL AMOUNT', 115, totalY + 3);
  doc.text(`${getCurrency(business?.settings)} ${(invoiceData.totalAmount || invoiceData.grandTotal || 0).toLocaleString('en-IN', {minimumFractionDigits: 2})}`, 191, totalY + 3, { align: 'right' });

  const termsY = totalY + 30;
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  doc.setTextColor(...primaryColor);
  doc.text('OFFICIAL SERVICE TERMS', 14, termsY);
  
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(8);
  doc.setTextColor(...grayText);
  
  const customTerms = invoiceData.termsAndConditions 
    ? invoiceData.termsAndConditions 
    : '• Please pay within the due date to avoid late fees.\n• Intellectual Property: Assets remain property of business until full payment.\n• Governance: Disputes subject to local jurisdiction.';

  const splitTerms = doc.splitTextToSize(customTerms, 110);
  doc.text(splitTerms, 14, termsY + 6);

  doc.setFontSize(7);
  doc.setTextColor(180, 180, 180);
  doc.text('THIS IS A COMPUTER GENERATED DOCUMENT.', 105, 285, { align: 'center' });

  doc.save(`Invoice_${invoiceData.invoiceNumber || 'New'}.pdf`);
};
